import { useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Review reply card — paste a Google review, get an AI-suggested reply.
//
// Manager flow:
//   1. Paste the review text. Optionally tag rating + reviewer name.
//   2. Click Generate. Edge function returns a reply tuned to tone.
//   3. Manager edits in the textarea if anything feels off.
//   4. Copy to clipboard. Paste it into Google Business Profile.
//
// No persistence — replies aren't stored. This is a one-shot tool the
// manager uses then discards. If we add direct Google Business Profile
// integration later, that's where review history lives.
export default function ReviewReplyCard({ restaurantName, restaurantType }) {
  const [reviewText, setReviewText]   = useState('')
  const [rating, setRating]           = useState('')      // empty | '1'..'5'
  const [reviewerName, setReviewerName] = useState('')
  const [phase, setPhase]             = useState('idle')  // idle | generating | ready | error
  const [reply, setReply]             = useState('')
  const [tone, setTone]               = useState(null)
  const [caveat, setCaveat]           = useState('')
  const [error, setError]             = useState(null)
  const [copied, setCopied]           = useState(false)

  async function generate() {
    if (phase === 'generating') return
    if (!reviewText.trim()) {
      setError('Paste the review text first.')
      setPhase('error')
      return
    }
    if (!restaurantName) {
      setError('Set your restaurant name in Settings first.')
      setPhase('error')
      return
    }

    setPhase('generating')
    setError(null)
    setCopied(false)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const payload = {
        review_text: reviewText.trim(),
        restaurant_name: restaurantName,
      }
      if (restaurantType) payload.restaurant_type = restaurantType
      if (reviewerName.trim()) payload.reviewer_name = reviewerName.trim()
      if (rating) payload.rating = Number(rating)

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-review-reply`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify(payload),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not generate a reply.')
        setPhase('error')
        return
      }
      setReply(data.reply || '')
      setTone(data.tone || null)
      setCaveat(data.caveat || '')
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ReviewReplyCard] generate failed', e)
      setError('Could not generate — please try again in a moment.')
      setPhase('error')
    }
  }

  async function copyReply() {
    if (!reply) return
    try {
      await navigator.clipboard.writeText(reply)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ReviewReplyCard] copy failed', e)
    }
  }

  function reset() {
    setReviewText('')
    setRating('')
    setReviewerName('')
    setReply('')
    setTone(null)
    setCaveat('')
    setError(null)
    setCopied(false)
    setPhase('idle')
  }

  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.warm

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-medium text-black/55">
        ✨ Review reply assistant
      </div>
      <p className="mt-1 text-xs text-black/60 leading-snug">
        Paste a Google review (or any platform) and get a reply tuned to the tone.
        Edit before posting — you&rsquo;re still the voice of the restaurant.
      </p>

      <div className="mt-3">
        <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="rr-review">
          Review text
        </label>
        <textarea
          id="rr-review"
          rows={4}
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Pulled pork was amazing, brisket was a bit dry though. Great service!"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="rr-rating">
            Rating (optional)
          </label>
          <select
            id="rr-rating"
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          >
            <option value="">— unspecified —</option>
            <option value="5">5 stars</option>
            <option value="4">4 stars</option>
            <option value="3">3 stars</option>
            <option value="2">2 stars</option>
            <option value="1">1 star</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="rr-name">
            Reviewer (optional)
          </label>
          <input
            id="rr-name"
            type="text"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            placeholder="First name"
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          />
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={generate}
          disabled={phase === 'generating' || !reviewText.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {phase === 'generating' ? 'Drafting…' : 'Generate reply'}
        </button>
        {phase !== 'idle' && (
          <button
            type="button"
            onClick={reset}
            className="rounded-lg border border-black/15 px-4 py-2 text-sm font-medium text-black/70 hover:bg-black/5"
          >
            Clear
          </button>
        )}
      </div>

      {phase === 'error' && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {phase === 'ready' && reply && (
        <div className={`mt-4 rounded-xl border p-3 ${toneStyle.border} ${toneStyle.bg}`}>
          <div className="flex items-baseline justify-between gap-2">
            <div className={`text-[11px] font-medium ${toneStyle.label}`}>
              Suggested reply · {tone || 'warm'}
            </div>
            <button
              type="button"
              onClick={copyReply}
              className={`rounded-md px-2 py-1 text-xs font-medium ${toneStyle.label} hover:bg-black/5`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <textarea
            rows={5}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="mt-2 w-full rounded-lg border border-black/15 bg-white/80 px-3 py-2 text-sm leading-snug outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
          />
          {caveat && (
            <div className="mt-2 text-xs text-black/60">
              <span className="font-medium text-black/70">Heads up:</span> {caveat}
            </div>
          )}
          <div className="mt-2 text-[11px] text-black/50 leading-snug">
            Edit anything that doesn&rsquo;t sound like you, then paste into Google Business Profile.
          </div>
        </div>
      )}
    </section>
  )
}

const TONE_STYLES = {
  warm: {
    border: 'border-blue-200',
    bg:     'bg-blue-50/40',
    label:  'text-blue-900/80',
  },
  celebratory: {
    border: 'border-emerald-200',
    bg:     'bg-emerald-50/50',
    label:  'text-emerald-900/80',
  },
  apologetic: {
    border: 'border-amber-200',
    bg:     'bg-amber-50/50',
    label:  'text-amber-900/80',
  },
  curious: {
    border: 'border-violet-200',
    bg:     'bg-violet-50/40',
    label:  'text-violet-900/80',
  },
}
